import { ColDef, GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import { tkColDef } from 'src/app/shared/ag-grid/tk-grid';

type Tone = 'success' | 'neutral' | 'warning' | 'danger' | 'purple' | 'muted';

/** One transaction from GET provider/financials/datatable. */
export interface FinancialRow {
  listing_id: number;
  booking_id: number;
  /** The Zai escrow item for the row; null when there is none (not an escrow booking). */
  item_id: number | null;
  /** "Increase for {booking_id}" on increase rows, null on every other row. */
  booking_title: string | null;
  /** The customer's username. */
  customer: string;
  customer_firstname: string | null;
  customer_lastname: string | null;
  /** Often null. */
  customer_business_name: string | null;
  customer_id: number | string;
  payment_id: number;
  lineitem_id: number;
  refund_id: number | null;
  release_id: number | null;
  disbursement_id: number | null;
  /** Set on increase rows. */
  increase_id: number | null;
  /** Set on decrease rows. */
  decrease_id: number | null;
  /** Set on chargeback rows. */
  chargeback_id: number | null;
  description: string;
  /** Booking status: complete, booked, cancelled, ... */
  status: string;
  /** "Suburb STATE" */
  collect: string | null;
  /** "Suburb STATE" */
  deliver: string | null;
  /** Decimal string, negative on decrease rows. */
  amount: string;
  /** Payout bank reference. */
  reference: string | null;
  /** "Quoted", "Book Now", "Refund", "Futile Fee", "Chargeback", "Increase - <reason>", "Decrease - <reason>". */
  fee_type: string;
  /** Freight listing category (e.g. "Pallets"); from fl.category_label on the datatable. */
  category_label: string | null;
  /** Unix seconds. */
  created_at: number;
  /** What the customer paid (the increase value on increase rows); null on decrease and chargeback rows. */
  sales_amount: string | null;
  /** Truckit's booking fee: sales_amount minus the provider's net value; null on decrease and chargeback rows. */
  truckit_fee: string | null;
  /** Escrow state: Released, Deposited, Refunded, a chargeback status, or null for non-escrow bookings. */
  payment_status: string | null;
}

/** The raw payment_status values the API filters on, with the label the filter shows for each. */
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'Released', label: 'Released' },
  { value: 'Deposited', label: 'Deposited' },
  { value: 'Refunded', label: 'Refunded' },
  { value: 'received', label: 'Chargeback – Received' },
  { value: 'active', label: 'Chargeback – Active' },
  { value: 'off_platform_chargeback', label: 'Chargeback – Off Platform' },
];

const PAYMENT_STATUS_TONES: Record<string, Tone> = { Released: 'success', Deposited: 'purple', Refunded: 'muted' };

/** "Released", "Deposited", "Refunded", or "Chargeback – <Status>" for anything else; "" when there is no status. */
export function formatPaymentStatus(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  if (value in PAYMENT_STATUS_TONES) {
    return value;
  }
  const status = formatStatus(value.replace(/_?chargeback$/i, ''));
  return status ? `Chargeback – ${status}` : 'Chargeback';
}

export function paymentStatusTone(value: unknown): Tone {
  // Any other value is a chargeback status.
  return typeof value === 'string' && value in PAYMENT_STATUS_TONES ? PAYMENT_STATUS_TONES[value] : 'danger';
}

/** Released, Deposited and chargeback statuses with an escrow item can be opened; Refunded and "—" cannot. */
export function isPaymentStatusClickable(row: Pick<FinancialRow, 'payment_status' | 'item_id'> | null | undefined): boolean {
  const status = row?.payment_status;
  return !!status && status !== 'Refunded' && row?.item_id !== null && row?.item_id !== undefined;
}

export const FINANCIAL_PAGE_SIZE = 10;

/** The booking statuses the API filters on, with the label the filter bar shows for each. */
export const FINANCIAL_STATUS_OPTIONS = [
  { value: 'booked', label: 'Booked' },
  { value: 'pre_collection', label: 'Pre-collection' },
  { value: 'provider_collected', label: 'Provider collected' },
  { value: 'customer_collected', label: 'Customer collected' },
  { value: 'provider_delivered', label: 'Provider delivered' },
  { value: 'customer_delivered', label: 'Customer delivered' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_LABELS = new Map(
  FINANCIAL_STATUS_OPTIONS.map((option) => [
    option.value,
    option.label,
  ]),
);

/** Newest first, used until the user picks a sort. */
export const FINANCIAL_DEFAULT_SORT = [
  { colId: 'created_at', sort: 'desc' as const },
];

/** A row has no single id of its own, so the id is every part that can tell two transactions apart. */
export function financialRowId(row: Partial<FinancialRow>): string {
  return [
    row.booking_id,
    row.lineitem_id,
    row.increase_id ?? '',
    row.decrease_id ?? '',
    row.chargeback_id ?? '',
    row.refund_id ?? '',
  ].join('-');
}

export function formatMoney(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return '';
  }
  const formatted = Math.abs(amount).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
}

/** The API sends created_at as unix seconds. */
export function formatUnixDate(value: unknown): string {
  const seconds = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(seconds)) {
    return '';
  }
  const date = new Date(seconds * 1000);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

export function formatStatus(value: unknown): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function badgeTone(status: string): Tone {
  if (status === 'complete' || status === 'customer_delivered') {
    return 'success';
  }
  if (status === 'cancelled') {
    return 'danger';
  }
  if (status === 'booked') {
    return 'warning';
  }
  return 'neutral';
}

function badge(label: string, tone: Tone, clickable = false): HTMLElement {
  const node = document.createElement(clickable ? 'button' : 'span');
  node.className = `fin-badge fin-badge--${tone}${clickable ? ' fin-badge--clickable' : ''}`;
  if (clickable) {
    (node as HTMLButtonElement).type = 'button';
  }
  const dot = document.createElement('span');
  dot.className = 'fin-dot';
  // textContent, never innerHTML: row values come from the API.
  node.append(dot, document.createTextNode(label));
  if (clickable) {
    // A report icon after the label tells the user the status can be opened.
    const icon = document.createElement('i');
    icon.className = 'bi bi-file-earmark-text fin-badge__icon';
    icon.setAttribute('aria-hidden', 'true');
    node.append(icon);
  }
  return node;
}

/** "Pallets · 12 Oct" under the booking id (Figma 6186:5776 Data row). */
export function bookingMeta(row: Partial<FinancialRow> | null | undefined): string {
  const category = (row?.category_label || '').trim();
  const seconds = Number(row?.created_at);
  const date = row?.created_at == null || Number.isNaN(seconds) ? '' : new Date(seconds * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  return [
    category,
    date,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** An increase row shows its title ("Increase for 13909") where the booking number would be; other rows show "#id". Category · date stacks under (desktop + mobile cards). */
function bookingRenderer(params: ICellRendererParams<FinancialRow>): HTMLElement | string {
  const title = params.data?.booking_title?.trim();
  const hasId = params.value !== null && params.value !== undefined;
  if (!title && !hasId) {
    return '';
  }
  const wrapper = document.createElement('span');
  wrapper.className = 'fin-booking';
  const primary = document.createElement('span');
  if (title) {
    primary.className = 'fin-booking-title';
    primary.textContent = title;
  } else {
    primary.textContent = `#${params.value}`;
  }
  wrapper.append(primary);
  const meta = bookingMeta(params.data);
  if (meta) {
    const metaNode = document.createElement('span');
    metaNode.className = 'fin-booking__meta text-muted';
    metaNode.textContent = meta;
    wrapper.append(metaNode);
  }
  return wrapper;
}

/** "First Last", or the username when the customer has no name. */
export function customerName(row: Partial<FinancialRow> | null | undefined): string {
  const name = [
    row?.customer_firstname,
    row?.customer_lastname,
  ]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return name || (row?.customer ?? '');
}

/** The customer's name, with the business name under it in smaller muted text when there is one. */
function customerRenderer(params: ICellRendererParams<FinancialRow>): HTMLElement | string {
  const name = customerName(params.data);
  if (!name) {
    return '';
  }
  const wrapper = document.createElement('span');
  wrapper.className = 'fin-customer';
  const nameNode = document.createElement('span');
  nameNode.textContent = name;
  wrapper.append(nameNode);
  const business = params.data?.customer_business_name?.trim();
  if (business) {
    const businessNode = document.createElement('span');
    businessNode.className = 'fin-customer__business text-muted';
    businessNode.textContent = business;
    wrapper.append(businessNode);
  }
  return wrapper;
}

function statusRenderer(params: ICellRendererParams): HTMLElement | string {
  const status = params.data?.status;
  return status ? badge(formatStatus(status), badgeTone(status)) : '';
}

function paymentStatusRenderer(params: ICellRendererParams): HTMLElement | string {
  const label = formatPaymentStatus(params.value);
  if (!label) {
    return '—';
  }
  const clickable = isPaymentStatusClickable(params.data);
  const node = badge(label, paymentStatusTone(params.value), clickable);
  if (clickable) {
    // The grid's context carries the component's opener, so this file stays free of Angular.
    node.addEventListener('click', () => params.context?.openDisbursementReport?.(params.data));
  }
  return node;
}

/** The row's invoice PDF: a link-styled button on every row that has a booking_id. */
function actionsRenderer(params: ICellRendererParams<FinancialRow>): HTMLElement | string {
  const bookingId = params.data?.booking_id;
  if (bookingId === null || bookingId === undefined) {
    return '';
  }
  const loading = !!params.context?.isInvoiceLoading?.(bookingId);
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'fin-action';
  button.disabled = loading;
  button.setAttribute('aria-label', `Download invoice PDF for booking ${bookingId}`);
  if (loading) {
    const spinner = document.createElement('span');
    spinner.className = 'spinner-border spinner-border-sm';
    spinner.setAttribute('aria-hidden', 'true');
    button.append(spinner);
  } else {
    const icon = document.createElement('img');
    icon.src = 'assets/icons/data-table/download.svg';
    icon.alt = '';
    icon.width = 14;
    icon.height = 14;
    button.append(icon);
  }
  button.append(document.createTextNode('PDF'));
  button.addEventListener('click', (event) => {
    // The click must not select or sort anything in the grid.
    event.stopPropagation();
    params.context?.downloadInvoice?.(params.data);
  });
  return button;
}

const textColumn = (colId: keyof FinancialRow, headerName: string, extra: ColDef = {}): ColDef => ({
  colId,
  field: colId,
  headerName,
  filter: 'agTextColumnFilter',
  ...extra,
});

const numberColumn = (colId: keyof FinancialRow, headerName: string, extra: ColDef = {}): ColDef => ({
  colId,
  field: colId,
  headerName,
  filter: 'agNumberColumnFilter',
  ...extra,
});

/** Id columns the API can sort and filter on. They stay out of the way until the user opens them. */
const idColumn = (colId: keyof FinancialRow, headerName: string): ColDef => numberColumn(colId, headerName, { hide: true, minWidth: 110 });

/**
 * Columns for the provider sales-invoice grid. Every column here can be sorted and filtered by
 * the API; ids match the field names it accepts. Columns marked hide stay in the Columns panel.
 */
export const FINANCIAL_COLUMNS: ColDef[] = [
  // Sorting and filtering stay on the booking number; the title has its own (hidden) column below.
  numberColumn('booking_id', 'Booking ID', {
    cellRenderer: bookingRenderer,
    // Clipboard/export matches the cell: primary line + category · date meta.
    valueFormatter: (p) => {
      const primary = p.data?.booking_title?.trim() || (p.value === null || p.value === undefined ? '' : `#${p.value}`);
      if (!primary) return '';
      const meta = bookingMeta(p.data);
      return meta ? `${primary}\n${meta}` : primary;
    },
    minWidth: 140,
  }),
  // Null on decrease and chargeback rows, which show "—".
  numberColumn('sales_amount', 'Sales Amount', {
    valueFormatter: (p) => formatMoney(p.value) || '—',
    cellClass: 'fin-strong',
    headerClass: 'ag-right-aligned-header',
    minWidth: 150,
  }),
  numberColumn('truckit_fee', 'Truckit Fee', {
    // Out of the way until the user turns it on in the Columns panel.
    hide: true,
    valueFormatter: (p) => formatMoney(p.value) || '—',
    cellClass: 'fin-strong',
    headerClass: 'ag-right-aligned-header',
    minWidth: 140,
  }),
  // The Paid Out column: it shows the escrow payment state.
  textColumn('payment_status', 'Paid Out', {
    cellRenderer: paymentStatusRenderer,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: PAYMENT_STATUS_OPTIONS.map((option) => option.value),
      valueFormatter: (p: { value: string | null }) => PAYMENT_STATUS_OPTIONS.find((option) => option.value === p.value)?.label ?? formatPaymentStatus(p.value),
    },
    minWidth: 190,
  }),
  textColumn('status', 'Job Status', {
    cellRenderer: statusRenderer,
    // Pick from the job statuses instead of typing one.
    filter: 'agSetColumnFilter',
    filterParams: {
      values: FINANCIAL_STATUS_OPTIONS.map((option) => option.value),
      valueFormatter: (p: { value: string | null }) => STATUS_LABELS.get(p.value ?? '') ?? formatStatus(p.value),
    },
    minWidth: 140,
  }),
  textColumn('description', 'Description', { minWidth: 200 }),
  // The API has no full-name field, so this column filters and sorts on the first name; the last name,
  // business name and username are separate columns in the Columns panel.
  textColumn('customer_firstname', 'Customer Name', {
    cellRenderer: customerRenderer,
    valueFormatter: (p) => customerName(p.data),
    minWidth: 180,
  }),
  textColumn('collect', 'Collection', { minWidth: 140 }),
  textColumn('deliver', 'Delivery', { minWidth: 140 }),

  {
    colId: 'created_at',
    field: 'created_at',
    headerName: 'Date',
    filter: 'agDateColumnFilter',
    // AG Grid's date filter has no "on or after"; declaring it lets the grid keep that type in its model, and
    // the API takes it as is. The predicate only matters for client-side filtering, which this grid never does.
    filterParams: {
      filterOptions: [
        'equals',
        'notEqual',
        'lessThan',
        'greaterThan',
        { displayKey: 'greaterThanOrEqual', displayName: 'On or after', numberOfInputs: 1, predicate: () => true },
        'inRange',
      ],
      debounceMs: 500,
    },
    valueFormatter: (p) => formatUnixDate(p.value),
    hide: true,
    minWidth: 120,
  },
  textColumn('customer_lastname', 'Customer Last Name', { hide: true, minWidth: 150 }),
  textColumn('customer_business_name', 'Business Name', { hide: true, minWidth: 160 }),
  textColumn('customer', 'Username', { hide: true, minWidth: 130 }),
  textColumn('booking_title', 'Booking Title', { hide: true, minWidth: 170 }),
  textColumn('fee_type', 'Fee Type', { hide: true, minWidth: 130 }),
  textColumn('reference', 'Reference', { hide: true, minWidth: 120 }),
  idColumn('listing_id', 'Listing ID'),
  textColumn('customer_id', 'Customer ID', { hide: true, minWidth: 120 }),
  idColumn('payment_id', 'Payment ID'),
  idColumn('lineitem_id', 'Line Item ID'),
  idColumn('refund_id', 'Refund ID'),
  idColumn('release_id', 'Release ID'),
  idColumn('disbursement_id', 'Disbursement ID'),
  idColumn('increase_id', 'Increase ID'),
  idColumn('decrease_id', 'Decrease ID'),
  idColumn('chargeback_id', 'Chargeback ID'),

  // Last, after the hidden ones in the Columns panel; the API has nothing to filter or sort here.
  {
    colId: 'actions',
    headerName: 'Actions',
    sortable: false,
    filter: false,
    floatingFilter: false,
    resizable: false,
    suppressMovable: true,
    cellRenderer: actionsRenderer,
    minWidth: 110,
    maxWidth: 130,
    flex: 0,
  },
];

export const FINANCIAL_DEFAULT_COL_DEF: ColDef = tkColDef({
  flex: 1,
  sortable: true,
  filter: false,
});

export const financialGetRowId = (params: GetRowIdParams<FinancialRow>): string => financialRowId(params.data);
